import { Controller } from 'stimulus'
import { trackEvent } from '../../src/javascripts/tracking'

export default class extends Controller {
  static targets = ["selectionMode", "selectionModeButton", "cancelButton", "selectedCount", "compareButton", "selectedThemes", "comparisonModal"]
  static values = {
    maxSelections: Number,
    currency: String,
    participants: Number
  }

  connect() {
    this.selectedThemeIds = new Set()
    this.maxSelections = this.maxSelectionsValue || 4
    this.updateUI()
  }

  toggleSelectionMode(event) {
    // Show selection mode
    this.selectionModeTarget.classList.remove('hidden')
    this.selectionModeButtonTarget.style.display = 'none'
    this.showSelectionCheckboxes()

    // Track opening of comparison mode
    trackEvent('Opened Activity Comparison Mode', {
      'currency': this.currencyValue || 'GBP',
      'participants': this.participantsValue || 20,
      'maxSelections': this.maxSelections
    });
  }

  cancelSelection(event) {
    // Hide selection mode and return to normal view
    this.selectionModeTarget.classList.add('hidden')
    this.selectionModeButtonTarget.style.display = 'block'
    this.hideSelectionCheckboxes()
    this.clearSelections()
  }

  showSelectionCheckboxes() {
    const themeCards = document.querySelectorAll('[data-theme-id]')
    console.log(`Found ${themeCards.length} theme cards`)
    themeCards.forEach((card, index) => {
      console.log(`Processing card ${index}:`, card)
      let checkbox = card.querySelector('.selection-checkbox')
      if (!checkbox) {
        checkbox = this.createSelectionCheckbox(card)
        const content = card.querySelector('.theme-card-content')
        if (content) {
          content.prepend(checkbox)
        } else {
          // Fallback: add to the card itself with relative positioning
          card.style.position = 'relative'
          card.prepend(checkbox)
        }
      }
      checkbox.classList.remove('hidden')
    })
  }

  hideSelectionCheckboxes() {
    const checkboxes = document.querySelectorAll('.selection-checkbox')
    checkboxes.forEach(checkbox => {
      checkbox.classList.add('hidden')
    })
  }

  createSelectionCheckbox(themeCard) {
    const themeId = themeCard.dataset.themeId
    const checkbox = document.createElement('div')
    checkbox.className = 'absolute z-10 selection-checkbox top-6 left-2'
    try {
      checkbox.innerHTML = `
        <input type="checkbox"
               id="theme_${themeId}"
               data-theme-id="${themeId}"
               data-action="change->activity-comparison#toggleSelection"
               class="form-checkbox h-10 w-10 border-2 border-gray-300 rounded focus:ring-wg-green-500 text-wg-green-300">
        <label for="theme_${themeId}" class="sr-only">Select this activity</label>
      `
    } catch (e) {
      // Fallback to basic input if template fails
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.dataset.themeId = themeId
      input.className = 'w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded'
      checkbox.appendChild(input)
    }
    return checkbox
  }

  toggleSelection(event) {
    const themeId = event.target.dataset.themeId
    const isChecked = event.target.checked

    if (isChecked && this.selectedThemeIds.size >= this.maxSelections) {
      event.target.checked = false
      alert(`You can select up to ${this.maxSelections} activities for comparison.`)
      return
    }

    if (isChecked) {
      this.selectedThemeIds.add(themeId)
      this.addThemeToComparison(themeId)

      // Track theme selection
      if (typeof mixpanel !== 'undefined') {
        const themeCard = document.querySelector(`[data-theme-id="${themeId}"]`)
        const themeName = themeCard ? themeCard.querySelector('p.text-xl.font-semibold, h3, .theme-title')?.textContent?.trim() : 'Unknown'

        trackEvent('Selected Activity for Comparison', {
          'themeName': themeName,
          'themeId': themeId,
          'currency': this.currencyValue || 'GBP',
          'participants': this.participantsValue || 20,
          'selectedCount': this.selectedThemeIds.size,
          'maxSelections': this.maxSelections
        });
      }
    } else {
      this.selectedThemeIds.delete(themeId)
      this.removeThemeFromComparison(themeId)

      // Track theme deselection
      if (typeof mixpanel !== 'undefined') {
        const themeCard = document.querySelector(`[data-theme-id="${themeId}"]`)
        const themeName = themeCard ? themeCard.querySelector('p.text-xl.font-semibold, h3, .theme-title')?.textContent?.trim() : 'Unknown'

        trackEvent('Deselected Activity from Comparison', {
          'themeName': themeName,
          'themeId': themeId,
          'currency': this.currencyValue || 'GBP',
          'participants': this.participantsValue || 20,
          'selectedCount': this.selectedThemeIds.size,
          'maxSelections': this.maxSelections
        });
      }
    }

    this.updateUI()
  }

  addThemeToComparison(themeId) {
    const themeCard = document.querySelector(`[data-theme-id="${themeId}"]`)
    const themeName = themeCard.querySelector('p.text-xl.font-semibold, h3, .theme-title').textContent.trim()
    const themeImage = themeCard.querySelector('img').src

    const themeElement = document.createElement('div')
    themeElement.className = 'flex items-center justify-between p-3 mb-2 rounded-lg selected-theme bg-blue-50'
    themeElement.dataset.themeId = themeId
    try {
      themeElement.innerHTML = `
        <div class="flex items-center">
          <img src="${themeImage}" alt="${themeName}" class="w-12 h-12 object-cover rounded mr-3">
          <span class="font-medium text-gray-900">${themeName}</span>
        </div>
        <button type="button"
                data-action="click->activity-comparison#removeSelection"
                data-theme-id="${themeId}"
                class="text-red-500 hover:text-red-700 p-1">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      `
    } catch (e) {
      // Fallback to simple text display
      const span = document.createElement('span')
      span.textContent = themeName
      span.className = 'font-medium text-gray-900'
      themeElement.appendChild(span)
    }

    this.selectedThemesTarget.appendChild(themeElement)
  }

  removeThemeFromComparison(themeId) {
    const themeElement = this.selectedThemesTarget.querySelector(`[data-theme-id="${themeId}"]`)
    if (themeElement) {
      themeElement.remove()
    }
  }

  removeSelection(event) {
    const themeId = event.currentTarget.dataset.themeId

    // Uncheck the checkbox
    const checkbox = document.querySelector(`input[data-theme-id="${themeId}"]`)
    if (checkbox) {
      checkbox.checked = false
    }

    // Remove from selected set
    this.selectedThemeIds.delete(themeId)
    this.removeThemeFromComparison(themeId)
    this.updateUI()
  }

  clearSelections() {
    this.selectedThemeIds.clear()
    this.selectedThemesTarget.innerHTML = ''

    // Uncheck all checkboxes
    const checkboxes = document.querySelectorAll('.selection-checkbox input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    this.updateUI()
  }

  updateUI() {
    const count = this.selectedThemeIds.size
    this.selectedCountTarget.textContent = count

    if (count >= 2) {
      this.compareButtonTarget.disabled = false
      this.compareButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    } else {
      this.compareButtonTarget.disabled = true
      this.compareButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  openComparisonModal() {
    if (this.selectedThemeIds.size < 2) {
      alert('Please select at least 2 activities to compare.')
      return
    }

    // Populate hidden form fields with selected theme IDs
    const form = this.comparisonModalTarget.querySelector('form')
    const existingThemeInputs = form.querySelectorAll('input[name="theme_ids[]"]')
    existingThemeInputs.forEach(input => input.remove())

    this.selectedThemeIds.forEach(themeId => {
      const hiddenInput = document.createElement('input')
      hiddenInput.type = 'hidden'
      hiddenInput.name = 'theme_ids[]'
      hiddenInput.value = themeId
      form.appendChild(hiddenInput)
    })

    // Track opening the team sharing modal
    if (typeof mixpanel !== 'undefined') {
      const selectedThemes = Array.from(this.selectedThemeIds).map(themeId => {
        const themeCard = document.querySelector(`[data-theme-id="${themeId}"]`)
        return themeCard ? themeCard.querySelector('p.text-xl.font-semibold, h3, .theme-title')?.textContent?.trim() : 'Unknown'
      })

      trackEvent('Opened Team Sharing Modal', {
        'currency': this.currencyValue || 'GBP',
        'participants': this.participantsValue || 20,
        'selectedCount': this.selectedThemeIds.size,
        'selectedThemes': selectedThemes,
        'selectedThemeIds': Array.from(this.selectedThemeIds)
      });
    }

    this.comparisonModalTarget.classList.remove('hidden')
  }

  closeComparisonModal() {
    this.comparisonModalTarget.classList.add('hidden')
  }

  trackComparisonCreated(event) {
    // Track team comparison creation with conversion event
    if (typeof mixpanel !== 'undefined') {
      const selectedThemes = Array.from(this.selectedThemeIds).map(themeId => {
        const themeCard = document.querySelector(`[data-theme-id="${themeId}"]`)
        return themeCard ? themeCard.querySelector('p.text-xl.font-semibold, h3, .theme-title')?.textContent?.trim() : 'Unknown'
      })

      const formData = new FormData(event.target)

      trackEvent('Created Team Activity Comparison', {
        'currency': this.currencyValue || 'GBP',
        'participants': this.participantsValue || 20,
        'selectedCount': this.selectedThemeIds.size,
        'selectedThemes': selectedThemes,
        'selectedThemeIds': Array.from(this.selectedThemeIds),
        'creatorName': formData.get('activity_comparison[name]'),
        'creatorEmail': formData.get('activity_comparison[email]'),
        'hasMessage': !!formData.get('activity_comparison[message]')
      }, {send_immediately: true}); // Use send_immediately for conversion events
    }
  }
}
